/**
 * Reservations Management Page
 * Two tabs: Upcoming (future dates) and Past (past dates)
 * Confirm action syncs to Google Calendar via Netlify function
 */

import React, { useState, useEffect } from 'react';
import {
  getReservations,
  approveReservation,
  cancelReservation,
  editReservation,
  type ReservationSummary,
} from '../services/reservations';
import { addReservationToCalendar } from '../services/googleCalendar';
import '../styles/pages/reservations-management.scss';

type Tab = 'upcoming' | 'past' | 'cancelled';

interface EditFormData {
  reservation_date: string;
  start_time: string;
  end_time: string;
  party_size: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  notes: string;
}

const ReservationsManagement: React.FC = () => {
  const [reservations, setReservations] = useState<ReservationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('upcoming');

  // Detail modal state
  const [viewingReservation, setViewingReservation] = useState<ReservationSummary | null>(null);

  // Edit modal state
  const [editingReservation, setEditingReservation] = useState<ReservationSummary | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    reservation_date: '',
    start_time: '',
    end_time: '',
    party_size: 1,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReservations();
      setReservations(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load reservations');
    } finally {
      setLoading(false);
    }
  };

  // Split into upcoming and past based on reservation_date
  const today = new Date().toISOString().split('T')[0];

  const cancelled = reservations
    .filter((r) => r.status === 'cancelled' || r.status === 'declined')
    .sort((a, b) => b.reservation_date.localeCompare(a.reservation_date) || b.start_time.localeCompare(a.start_time));

  const upcoming = reservations
    .filter((r) => r.reservation_date >= today && r.status !== 'cancelled' && r.status !== 'declined')
    .sort((a, b) => a.reservation_date.localeCompare(b.reservation_date) || a.start_time.localeCompare(b.start_time));

  const past = reservations
    .filter((r) => r.reservation_date < today && r.status !== 'cancelled' && r.status !== 'declined')
    .sort((a, b) => b.reservation_date.localeCompare(a.reservation_date) || b.start_time.localeCompare(a.start_time));

  const displayed = activeTab === 'upcoming' ? upcoming : activeTab === 'past' ? past : cancelled;

  // Confirm a pending reservation (updates Supabase + creates Google Calendar event)
  const handleConfirm = async (id: string) => {
    try {
      setProcessing(id);
      setError(null);
      await approveReservation(id);
      setSuccessMessage('Reservation confirmed and added to Google Calendar');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm reservation');
    } finally {
      setProcessing(null);
    }
  };

  // Cancel a reservation
  const handleCancel = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) return;

    try {
      setProcessing(id);
      setError(null);
      await cancelReservation(id);
      setSuccessMessage('Reservation cancelled');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel reservation');
    } finally {
      setProcessing(null);
    }
  };

  // Re-add an already-confirmed reservation to Google Calendar
  const handleReaddToCalendar = async (reservation: ReservationSummary) => {
    try {
      setProcessing(reservation.id);
      setError(null);
      await addReservationToCalendar({
        id: reservation.id,
        first_name: reservation.first_name,
        last_name: reservation.last_name,
        email: reservation.email,
        phone: reservation.phone,
        reservation_date: reservation.reservation_date,
        start_time: reservation.start_time,
        end_time: reservation.end_time,
        party_size: reservation.party_size,
        notes: reservation.notes,
        reservation_type: reservation.reservation_type,
      });
      setSuccessMessage('Event re-added to Google Calendar');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to add to Google Calendar');
    } finally {
      setProcessing(null);
    }
  };

  // Open edit modal
  const handleEditClick = (reservation: ReservationSummary) => {
    setEditingReservation(reservation);
    setEditForm({
      reservation_date: reservation.reservation_date,
      start_time: reservation.start_time.slice(0, 5),
      end_time: reservation.end_time.slice(0, 5),
      party_size: reservation.party_size,
      first_name: reservation.first_name,
      last_name: reservation.last_name,
      email: reservation.email,
      phone: reservation.phone || '',
      notes: reservation.notes || '',
    });
  };

  // Save edit
  const handleEditSave = async () => {
    if (!editingReservation) return;

    try {
      setProcessing(editingReservation.id);
      setError(null);
      await editReservation(editingReservation.id, {
        reservation_date: editForm.reservation_date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        party_size: editForm.party_size,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: editForm.phone,
        notes: editForm.notes,
      });
      setEditingReservation(null);
      setSuccessMessage('Reservation updated, email sent, and calendar synced');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to update reservation');
    } finally {
      setProcessing(null);
    }
  };

  // Format date: "Fri, Mar 6"
  const formatDate = (dateString: string): string => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time: "6:00 PM"
  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Location display
  const formatLocation = (type: string): string => {
    return type === 'back_room' ? 'Backroom' : 'Entire Shop';
  };

  // Status badge class
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'confirmed':
        return 'badge--success';
      case 'pending':
        return 'badge--warning';
      case 'declined':
        return 'badge--error';
      case 'cancelled':
        return 'badge--neutral';
      default:
        return '';
    }
  };

  return (
    <div className="page-reservations-management">
      <div className="page-reservations-management__header">
        <h1 className="page-reservations-management__title">Reservations</h1>
      </div>

      {/* Tabs */}
      <div className="reservations-tabs">
        <button
          className={`reservations-tabs__tab ${activeTab === 'upcoming' ? 'reservations-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          Upcoming
          <span className="reservations-tabs__count">{upcoming.length}</span>
        </button>
        <button
          className={`reservations-tabs__tab ${activeTab === 'past' ? 'reservations-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
          <span className="reservations-tabs__count">{past.length}</span>
        </button>
        <button
          className={`reservations-tabs__tab ${activeTab === 'cancelled' ? 'reservations-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('cancelled')}
        >
          Cancelled
          <span className="reservations-tabs__count">{cancelled.length}</span>
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert--success" role="status">
          {successMessage}
          <button className="alert__close" onClick={() => setSuccessMessage(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
          <button className="alert__close" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Reservations Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading reservations...</p>
        </div>
      ) : displayed.length === 0 ? (
        <div className="empty-state">
          <i
            className="fa-sharp fa-thin fa-calendar-xmark empty-state__icon"
            aria-hidden="true"
          ></i>
          <h2 className="empty-state__title">
            {activeTab === 'upcoming' ? 'No Upcoming Reservations' : activeTab === 'past' ? 'No Past Reservations' : 'No Cancelled Reservations'}
          </h2>
          <p className="empty-state__description">
            {activeTab === 'upcoming'
              ? 'No reservations scheduled for the future'
              : activeTab === 'past'
              ? 'No past reservations found'
              : 'No cancelled reservations found'}
          </p>
        </div>
      ) : (
        <div className="reservations-table-wrapper">
          <table className="reservations-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Name</th>
                <th>Party Size</th>
                <th>Location</th>
                <th>Notes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((reservation) => (
                <tr key={reservation.id} className="reservations-table__row reservations-table__row--clickable" onClick={() => setViewingReservation(reservation)}>
                  <td>
                    <span className="reservation-date__day">
                      {formatDate(reservation.reservation_date)}
                    </span>
                  </td>
                  <td>
                    <span className="reservation-date__time">
                      {formatTime(reservation.start_time)} - {formatTime(reservation.end_time)}
                    </span>
                  </td>
                  <td>
                    <span className="reservation-guest__name">
                      {reservation.first_name} {reservation.last_name}
                    </span>
                  </td>
                  <td>
                    <span className="party-size">{reservation.party_size}</span>
                  </td>
                  <td>{formatLocation(reservation.reservation_type)}</td>
                  <td>
                    {reservation.notes ? (
                      <span className="reservation-notes" title={reservation.notes}>
                        {reservation.notes}
                      </span>
                    ) : (
                      <span className="reservation-notes--empty">—</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusClass(reservation.status)}`}>
                      {reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="reservation-actions">
                      {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                        <button
                          className="btn btn--sm btn--outline"
                          onClick={() => handleEditClick(reservation)}
                          disabled={processing === reservation.id}
                        >
                          <i className="fa-sharp fa-thin fa-pencil" aria-hidden="true"></i>
                          Edit
                        </button>
                      )}
                      {reservation.status === 'pending' && (
                        <>
                          <button
                            className="btn btn--sm btn--primary"
                            onClick={() => handleConfirm(reservation.id)}
                            disabled={processing === reservation.id}
                          >
                            {processing === reservation.id ? 'Confirming...' : 'Confirm'}
                          </button>
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={() => handleCancel(reservation.id)}
                            disabled={processing === reservation.id}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {reservation.status === 'confirmed' && (
                        <>
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={() => handleCancel(reservation.id)}
                            disabled={processing === reservation.id}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn--sm btn--outline"
                            onClick={() => handleReaddToCalendar(reservation)}
                            disabled={processing === reservation.id}
                          >
                            <i className="fa-sharp fa-thin fa-calendar-plus" aria-hidden="true"></i>
                            {processing === reservation.id ? 'Adding...' : 'Re-add to Calendar'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Reservation Detail Modal */}
      {viewingReservation && (
        <div className="modal-overlay" onClick={() => setViewingReservation(null)}>
          <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Reservation Details</h2>
              <button
                className="modal__close"
                onClick={() => setViewingReservation(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal__body">
              <div className="detail-grid">
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Guest</span>
                  <span className="detail-grid__value">{viewingReservation.first_name} {viewingReservation.last_name}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Email</span>
                  <span className="detail-grid__value">{viewingReservation.email}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Phone</span>
                  <span className="detail-grid__value">{viewingReservation.phone || '—'}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Date</span>
                  <span className="detail-grid__value">{formatDate(viewingReservation.reservation_date)}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Time</span>
                  <span className="detail-grid__value">{formatTime(viewingReservation.start_time)} - {formatTime(viewingReservation.end_time)}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Party Size</span>
                  <span className="detail-grid__value">{viewingReservation.party_size}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Location</span>
                  <span className="detail-grid__value">{formatLocation(viewingReservation.reservation_type)}</span>
                </div>
                <div className="detail-grid__item">
                  <span className="detail-grid__label">Status</span>
                  <span className="detail-grid__value">
                    <span className={`badge ${getStatusClass(viewingReservation.status)}`}>
                      {viewingReservation.status.charAt(0).toUpperCase() + viewingReservation.status.slice(1)}
                    </span>
                  </span>
                </div>
              </div>
              {viewingReservation.notes && (
                <div className="detail-notes">
                  <span className="detail-notes__label">Notes</span>
                  <p className="detail-notes__text">{viewingReservation.notes}</p>
                </div>
              )}
            </div>
            <div className="modal__footer">
              <button
                className="btn btn--outline"
                onClick={() => setViewingReservation(null)}
              >
                Close
              </button>
              {(viewingReservation.status === 'pending' || viewingReservation.status === 'confirmed') && (
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    handleEditClick(viewingReservation);
                    setViewingReservation(null);
                  }}
                >
                  <i className="fa-sharp fa-thin fa-pencil" aria-hidden="true"></i>
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Reservation Modal */}
      {editingReservation && (
        <div className="modal-overlay" onClick={() => setEditingReservation(null)}>
          <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Edit Reservation</h2>
              <button
                className="modal__close"
                onClick={() => setEditingReservation(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal__body">
              <div className="edit-form">
                <div className="edit-form__row">
                  <div className="form-field">
                    <label htmlFor="edit-first-name" className="form-field__label">First Name</label>
                    <input
                      id="edit-first-name"
                      type="text"
                      className="form-field__input"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="edit-last-name" className="form-field__label">Last Name</label>
                    <input
                      id="edit-last-name"
                      type="text"
                      className="form-field__input"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="edit-form__row">
                  <div className="form-field">
                    <label htmlFor="edit-email" className="form-field__label">Email</label>
                    <input
                      id="edit-email"
                      type="email"
                      className="form-field__input"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="edit-phone" className="form-field__label">Phone</label>
                    <input
                      id="edit-phone"
                      type="tel"
                      className="form-field__input"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="edit-form__row">
                  <div className="form-field">
                    <label htmlFor="edit-date" className="form-field__label">Date</label>
                    <input
                      id="edit-date"
                      type="date"
                      className="form-field__input"
                      value={editForm.reservation_date}
                      onChange={(e) => setEditForm({ ...editForm, reservation_date: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="edit-party-size" className="form-field__label">Party Size</label>
                    <input
                      id="edit-party-size"
                      type="number"
                      min="1"
                      max="50"
                      className="form-field__input"
                      value={editForm.party_size}
                      onChange={(e) => setEditForm({ ...editForm, party_size: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>

                <div className="edit-form__row">
                  <div className="form-field">
                    <label htmlFor="edit-start-time" className="form-field__label">Start Time</label>
                    <input
                      id="edit-start-time"
                      type="time"
                      className="form-field__input"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="edit-end-time" className="form-field__label">End Time</label>
                    <input
                      id="edit-end-time"
                      type="time"
                      className="form-field__input"
                      value={editForm.end_time}
                      onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="edit-notes" className="form-field__label">Notes</label>
                  <textarea
                    id="edit-notes"
                    className="form-field__textarea"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    placeholder="Special requests or notes..."
                  />
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <button
                className="btn btn--outline"
                onClick={() => setEditingReservation(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={handleEditSave}
                disabled={processing === editingReservation.id}
              >
                {processing === editingReservation.id ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationsManagement;
